/**
 * SegmentedProgressBar.tsx
 * 
 * A segmented progress bar component that shows progress through journey pages.
 * Features:
 * - Dynamically sized segments based on total number of pages
 * - Shows current step with a badge (e.g., "11/14")
 * - Displays percentage completion
 * - Green segments for completed steps, gray for remaining
 * - Smooth animations
 * 
 * Props injected by JourneyPage:
 * - currentStep: number (current page index, 1-based)
 * - totalSteps: number (total pages in journey)
 */

import React from 'react';

interface SegmentedProgressBarProps {
  currentStep?: number;
  totalSteps?: number;
  isBuilderMode?: boolean;
  value?: {
    color?: string;
    showPercentage?: boolean;
    showStepBadge?: boolean;
  };
  onChange?: (val: any) => void;
  disabled?: boolean;
}

const SegmentedProgressBar: React.FC<SegmentedProgressBarProps> = ({
  currentStep = 11,
  totalSteps = 14,
  isBuilderMode = false,
  value = {},
  onChange,
  disabled = false,
}) => {
  const color = value.color || '#065a4e'; // intellend-green
  const showPercentage = value.showPercentage ?? true;
  const showStepBadge = value.showStepBadge ?? true;

  // Calculate percentage
  const percentage = totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0;

  const update = (patch: any) => onChange?.({ ...value, ...patch });

  // Journey mode rendering
  if (!isBuilderMode) {
    return (
      <div className="w-full py-1">
        <div className="flex items-center justify-between mb-1.5">
          {/* Step indicator */}
          <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            STEP {currentStep}/{totalSteps}
          </div>

          {/* Percentage */}
          {showPercentage && (
            <div className="text-xl font-bold" style={{ color }}>
              {percentage}%
            </div>
          )}
        </div>

        {/* Segmented progress bar */}
        <div className="relative flex items-center gap-0.5">
          {/* Segments */}
          {Array.from({ length: totalSteps }).map((_, index) => {
            const isCompleted = index < currentStep;
            
            return (
              <div
                key={index}
                className="flex-1 h-1.5 rounded-sm transition-all duration-500 ease-out"
                style={{
                  backgroundColor: isCompleted ? color : '#d1d5db',
                }}
              />
            );
          })}

          {/* Current step badge */}
          {showStepBadge && (
            <div
              className="absolute top-[-6px] flex items-center justify-center px-1.5 py-0.5 rounded text-white text-[10px] font-bold shadow-md transition-all duration-500"
              style={{
                backgroundColor: color,
                left: `${(currentStep / totalSteps) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {currentStep}/{totalSteps}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Builder mode rendering
  return (
    <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
      {/* Preview */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium text-gray-500">
            STEP {currentStep}/{totalSteps}
          </div>
          {showPercentage && (
            <div className="text-2xl font-bold" style={{ color }}>
              {percentage}%
            </div>
          )}
        </div>

        <div className="relative flex items-center gap-1">
          {Array.from({ length: totalSteps }).map((_, index) => {
            const isCompleted = index < currentStep;
            
            return (
              <div
                key={index}
                className="flex-1 h-2 rounded-full transition-all duration-500"
                style={{
                  backgroundColor: isCompleted ? color : '#e5e7eb',
                  opacity: isCompleted ? 1 : 0.5,
                }}
              />
            );
          })}

          {showStepBadge && (
            <div
              className="absolute top-[-8px] flex items-center justify-center px-2 py-1 rounded-md text-white text-xs font-bold shadow-lg"
              style={{
                backgroundColor: color,
                left: `${(currentStep / totalSteps) * 100}%`,
                transform: 'translateX(-50%)',
              }}
            >
              {currentStep}/{totalSteps}
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold text-gray-600 uppercase">
            Color
          </label>
          <input
            type="color"
            value={color}
            onChange={(e) => update({ color: e.target.value })}
            disabled={disabled}
            className="w-full h-10 rounded cursor-pointer border border-gray-300"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="showPercentage"
            checked={showPercentage}
            onChange={(e) => update({ showPercentage: e.target.checked })}
            disabled={disabled}
            className="w-4 h-4 rounded border-gray-300"
          />
          <label htmlFor="showPercentage" className="text-sm text-gray-700">
            Show percentage
          </label>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="showStepBadge"
            checked={showStepBadge}
            onChange={(e) => update({ showStepBadge: e.target.checked })}
            disabled={disabled}
            className="w-4 h-4 rounded border-gray-300"
          />
          <label htmlFor="showStepBadge" className="text-sm text-gray-700">
            Show step badge
          </label>
        </div>
      </div>

      <div className="text-xs text-gray-500 italic">
        Note: Segment size automatically adjusts based on total pages in journey
      </div>
    </div>
  );
};

export default SegmentedProgressBar;
